import React, { useEffect, useMemo, useRef, useState } from "react";
import "./InstallNextScenes.css";

const DISMISS_KEY = "nextscenes:pwa-install-dismissed-until";
const DISMISS_DAYS = 7;

function isRunningStandalone() {
  const standaloneDisplay =
    window.matchMedia &&
    window.matchMedia("(display-mode: standalone)").matches;

  const iosStandalone = window.navigator.standalone === true;

  return standaloneDisplay || iosStandalone;
}

function isIOSDevice() {
  const userAgent = window.navigator.userAgent || "";
  const classicIOS = /iPad|iPhone|iPod/i.test(userAgent);
  const modernIPad =
    /Macintosh/i.test(userAgent) && window.navigator.maxTouchPoints > 1;

  return classicIOS || modernIPad;
}

function wasRecentlyDismissed() {
  try {
    const dismissedUntil = Number(
      window.localStorage.getItem(DISMISS_KEY) || "0"
    );

    return Number.isFinite(dismissedUntil) && dismissedUntil > Date.now();
  } catch {
    return false;
  }
}

function rememberDismissal() {
  try {
    const dismissedUntil =
      Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;

    window.localStorage.setItem(DISMISS_KEY, String(dismissedUntil));
  } catch {
    // Installation remains usable when local storage is unavailable.
  }
}

function clearDismissal() {
  try {
    window.localStorage.removeItem(DISMISS_KEY);
  } catch {
    // Nothing else is required.
  }
}

export default function InstallNextScenes() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [installState, setInstallState] = useState("ready");
  const [statusMessage, setStatusMessage] = useState("");
  const successTimerRef = useRef(null);

  const iosDevice = useMemo(() => isIOSDevice(), []);

  useEffect(() => {
    if (isRunningStandalone() || wasRecentlyDismissed()) {
      return undefined;
    }

    let iosTimer;

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setInstallState("ready");
      setStatusMessage("");

      window.setTimeout(() => {
        setVisible(true);
      }, 2500);
    };

    const handleInstalled = () => {
      clearDismissal();
      setDeferredPrompt(null);
      setShowIOSHelp(false);
      setInstallState("installed");
      setStatusMessage("Installed successfully. You can now open NextScenes from your Applications folder, Dock, or browser app menu.");
      setVisible(true);
      if (successTimerRef.current) window.clearTimeout(successTimerRef.current);
      successTimerRef.current = window.setTimeout(() => setVisible(false), 12000);
    };

    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt
    );
    window.addEventListener("appinstalled", handleInstalled);

    if (iosDevice) {
      iosTimer = window.setTimeout(() => {
        if (!isRunningStandalone()) {
          setVisible(true);
        }
      }, 6000);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleInstalled);

      if (iosTimer) {
        window.clearTimeout(iosTimer);
      }
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current);
      }
    };
  }, [iosDevice]);

  const dismiss = () => {
    rememberDismissal();
    if (installState !== "installed") rememberDismissal();
    setVisible(false);
    setShowIOSHelp(false);
    setStatusMessage("");
  };

  const install = async () => {
    if (iosDevice && !deferredPrompt) {
      setShowIOSHelp(true);
      setInstallState("ios-help");
      setStatusMessage("");
      return;
    }

    if (!deferredPrompt) {
      setInstallState("unavailable");
      setStatusMessage("The browser installation option is not available at this moment. Please use the install icon in the browser address bar.");
      return;
    }

    setInstallState("confirming");
    setStatusMessage("Please confirm the installation in the browser window now displayed.");

    try {
      await deferredPrompt.prompt();
      setInstallState("installing");
      setStatusMessage("Chrome is completing the installation. NextScenes may open in its own window.");
      const choice = await deferredPrompt.userChoice;

      if (choice?.outcome === "accepted") {
        clearDismissal();
        window.setTimeout(() => {
          setInstallState((current) => current === "installing" ? "installed" : current);
          setStatusMessage((current) => current.includes("completing") ? "Installed successfully. NextScenes should now be available among your applications." : current);
        }, 3500);
      } else {
        setInstallState("cancelled");
        setStatusMessage("Installation was not completed. You may try again whenever you are ready.");
      }
    } catch (error) {
      console.error("NextScenes installation prompt failed:", error);
      setInstallState("error");
      setStatusMessage("The installation could not be completed. Please try again or use the install icon in the browser address bar.");
    } finally {
      setDeferredPrompt(null);
    }
  };

  if (!visible || isRunningStandalone()) {
    return null;
  }

  return (
    <aside
      className={`nextscenes-install nextscenes-install--${installState}`}
      aria-label="Install NextScenes"
      aria-live="polite"
      aria-busy={installState === "confirming" || installState === "installing"}
    >
      <button
        type="button"
        className="nextscenes-install__close"
        onClick={dismiss}
        aria-label="Dismiss installation invitation"
        title="Not now"
      >
        ×
      </button>

      <div className="nextscenes-install__mark" aria-hidden="true">
        <img src="/logo192.png" alt="" />
      </div>

      <div className="nextscenes-install__content">
        <p className="nextscenes-install__eyebrow">
          {installState === "installed" ? "Installation complete" : "NextScenes on your device"}
        </p>

        <h2>{installState === "installed" ? "NextScenes is ready" : "Carry the stories with you"}</h2>

        {!showIOSHelp ? (
          <>
            <p>
              Install NextScenes for quicker access to reading, writing and
              scene proposals.
            </p>

            <p className="nextscenes-install__fr" lang="fr">
              Installez NextScenes pour accéder plus rapidement aux lectures,
              à l’écriture et aux propositions de scènes.
            </p>
          </>
        ) : (
          <div className="nextscenes-install__instructions">
            <p>
              On iPhone or iPad: tap <strong>Share</strong>, choose{" "}
              <strong>Add to Home Screen</strong>, then tap{" "}
              <strong>Add</strong>.
            </p>

            <p className="nextscenes-install__fr" lang="fr">
              Sur iPhone ou iPad : touchez <strong>Partager</strong>, choisissez{" "}
              <strong>Sur l’écran d’accueil</strong>, puis touchez{" "}
              <strong>Ajouter</strong>.
            </p>
          </div>
        )}


        {statusMessage && (
          <div className={`nextscenes-install__status nextscenes-install__status--${installState}`} role="status">
            {(installState === "confirming" || installState === "installing") && <span className="nextscenes-install__spinner" aria-hidden="true" />}
            {installState === "installed" && <span className="nextscenes-install__success-mark" aria-hidden="true">✓</span>}
            <div>
              <p>{statusMessage}</p>
              {installState === "installed" && <p className="nextscenes-install__fr" lang="fr">NextScenes est installé et se trouve maintenant parmi vos applications.</p>}
            </div>
          </div>
        )}

        <div className="nextscenes-install__actions">
          {installState === "ready" && !showIOSHelp && (
            <button
              type="button"
              className="nextscenes-install__primary"
              onClick={install}
            >
              Install NextScenes
              <span lang="fr">Installer</span>
            </button>
          )}

          {showIOSHelp && (
            <button
              type="button"
              className="nextscenes-install__primary"
              onClick={dismiss}
            >
              Understood
              <span lang="fr">Compris</span>
            </button>
          )}

          {installState === "cancelled" && (
            <button type="button" className="nextscenes-install__primary" onClick={() => { setInstallState("ready"); setStatusMessage(""); }}>
              Try again <span lang="fr">Réessayer</span>
            </button>
          )}
          {installState === "installed" && (
            <button type="button" className="nextscenes-install__primary" onClick={() => setVisible(false)}>
              Done <span lang="fr">Terminé</span>
            </button>
          )}
          {installState !== "confirming" && installState !== "installing" && installState !== "installed" && (
            <button type="button" className="nextscenes-install__secondary" onClick={dismiss}>
              Not now · Plus tard
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}
